import React, { useState, useEffect, useRef } from 'react';
import { Device, Call } from '@twilio/voice-sdk';
import { useAuth0 } from '@auth0/auth0-react';
import { Channel } from 'pusher-js';
import { threadChannel } from '@dreamai/types';
import api from '../../../../api';
import { useCompany } from '../../../../contexts/CompanyContext';
import { usePusher } from '../../../../contexts/PusherContext';
import { PUSHER_ENV } from '../../../../lib/pusherClient';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPhone,
  faPhoneSlash,
  faSpinner,
} from '@fortawesome/free-solid-svg-icons';
import { describeTwilioDialError } from './twilioDialError';
import {
  buildCallStatusPayload,
  resolveCallEndedBy,
  type CallEndedBy,
} from './callEndReason';
import './HumanTaskPhoneDialer.scss';

interface HumanTaskPhoneDialerProps {
  taskId: string;
  phoneNumber: string; // Initial phone number from canvas props
  contactName?: string;
  /** The requester cancelled the task — show wind-down guidance, block new calls */
  cancelled?: boolean;
  /** Notifies the parent whenever a call becomes active / inactive */
  onCallActiveChange?: (active: boolean) => void;
}

type CallStatus =
  | 'idle'
  | 'loading'
  | 'initializing'
  | 'connecting'
  | 'connected'
  | 'ended'
  | 'error';

// Payload of the backend's 'call-transcript-update' Pusher event (see
// dream-be lib/callTranslation.ts). Partial lines share an utteranceId with
// their final version so they can be revised in place as recognition improves.
interface CallTranscriptUpdateEvent {
  callId: string;
  utteranceId: string;
  side: 'caller' | 'worker';
  text: string;
  isFinal: boolean;
}

const CALL_TRANSCRIPT_UPDATE_EVENT = 'call-transcript-update';

// A reply/opener suggestion: the English (so the operator can pick) plus the
// target-language phrasing (to read aloud).
interface SuggestedReply {
  english: string;
  translated: string;
}

// Payload of the backend's 'call-suggested-replies' Pusher event, sent after
// each finalized caller line. An empty suggestions array clears the panel.
interface CallSuggestedRepliesEvent {
  callId: string;
  utteranceId: string;
  suggestions: SuggestedReply[];
}

const CALL_SUGGESTED_REPLIES_EVENT = 'call-suggested-replies';

const HumanTaskPhoneDialer: React.FC<HumanTaskPhoneDialerProps> = ({
  taskId,
  phoneNumber: initialPhoneNumber,
  contactName: initialContactName,
  cancelled = false,
  onCallActiveChange,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const { opsRole } = useCompany();
  const isOpsAdmin = opsRole === 'ops_admin';
  const [guidanceEnabled, setGuidanceEnabled] = useState<boolean>(!isOpsAdmin);
  const [callStatus, setCallStatus] = useState<CallStatus>('loading');
  const [callDuration, setCallDuration] = useState<number>(0);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [callId, setCallId] = useState<string>('');
  const [phoneNumber, setPhoneNumber] = useState<string>(initialPhoneNumber);
  const [contactName, setContactName] = useState<string | undefined>(
    initialContactName,
  );
  const [transcriptionInput, setTranscriptionInput] = useState<string>('');
  const [translationEnabled, setTranslationEnabled] = useState<boolean>(false);
  const [targetLanguage, setTargetLanguage] = useState<string>('');
  const [transcriptLines, setTranscriptLines] = useState<
    CallTranscriptUpdateEvent[]
  >([]);
  const [suggestedReplies, setSuggestedReplies] = useState<SuggestedReply[]>(
    [],
  );
  const [openers, setOpeners] = useState<SuggestedReply[]>([]);
  const [openersLoading, setOpenersLoading] = useState<boolean>(false);
  const [typedMessage, setTypedMessage] = useState<string>('');
  const [translatedMessage, setTranslatedMessage] = useState<string>('');
  const [isTranslating, setIsTranslating] = useState<boolean>(false);
  const [translationError, setTranslationError] = useState<string>('');
  const [isRequestingGuidance, setIsRequestingGuidance] =
    useState<boolean>(false);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [threadId, setThreadId] = useState<string | null>(null);
  const [agentId, setAgentId] = useState<string | null>(null);
  const [isEditingPhone, setIsEditingPhone] = useState<boolean>(false);
  const [editedPhoneNumber, setEditedPhoneNumber] =
    useState<string>(initialPhoneNumber);

  const deviceRef = useRef<Device | null>(null);
  const callRef = useRef<Call | null>(null);
  // Set by hangUpCall() so the SDK's generic `disconnect` can be attributed.
  const localHangUpRef = useRef<boolean>(false);
  const callErrorRef = useRef<boolean>(false);
  const durationIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const transcriptPanelRef = useRef<HTMLDivElement | null>(null);
  const transcriptChannelRef = useRef<Channel | null>(null);
  const pusherContext = usePusher(true);

  // Fetch latest task details on mount
  useEffect(() => {
    const fetchTaskDetails = async () => {
      try {
        const token = await getAccessTokenSilently();
        const response = await api.get(`/human-tasks/${taskId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.data.success && response.data.task) {
          const task = response.data.task;
          const details = task.taskDetails;

          // Store thread and agent IDs for system message
          setThreadId(task.threadId);
          setAgentId(task.createdByAgentId);

          // Update with latest data from database
          if (details.phoneNumber) {
            setPhoneNumber(details.phoneNumber);
          }
          if (details.contactName) {
            setContactName(details.contactName);
          }
        }

        setCallStatus('idle');
      } catch (error) {
        console.error('Failed to fetch task details:', error);
        // Fall back to prop values if fetch fails
        setCallStatus('idle');
      }
    };

    fetchTaskDetails();
  }, [taskId, getAccessTokenSilently]);

  // Live translation transcript: subscribe to the task thread channel and
  // upsert lines by utteranceId so a partial line is revised in place until
  // its final version arrives.
  useEffect(() => {
    if (
      !translationEnabled ||
      !callId ||
      !threadId ||
      !pusherContext?.pusherInstance
    ) {
      return;
    }

    const channelName = threadChannel(PUSHER_ENV, threadId);
    const channel = pusherContext.pusherInstance.subscribe(channelName);
    transcriptChannelRef.current = channel;

    const handleTranscriptUpdate = (event: CallTranscriptUpdateEvent) => {
      if (event.callId !== callId) return;
      // Only the other party's speech is useful here — the operator already
      // knows what they said, so their own lines are dropped.
      if (event.side !== 'caller') return;
      setTranscriptLines((prev) => {
        const index = prev.findIndex(
          (line) => line.utteranceId === event.utteranceId,
        );
        if (index === -1) return [...prev, event];
        // Never let a late partial overwrite the final version
        if (prev[index].isFinal && !event.isFinal) return prev;
        const next = [...prev];
        next[index] = event;
        return next;
      });
    };

    const handleSuggestedReplies = (event: CallSuggestedRepliesEvent) => {
      if (event.callId !== callId) return;
      setSuggestedReplies(event.suggestions);
    };

    channel.bind(CALL_TRANSCRIPT_UPDATE_EVENT, handleTranscriptUpdate);
    channel.bind(CALL_SUGGESTED_REPLIES_EVENT, handleSuggestedReplies);

    return () => {
      channel.unbind(CALL_TRANSCRIPT_UPDATE_EVENT, handleTranscriptUpdate);
      channel.unbind(CALL_SUGGESTED_REPLIES_EVENT, handleSuggestedReplies);
      transcriptChannelRef.current = null;
    };
  }, [translationEnabled, callId, threadId, pusherContext]);

  // Keep the transcript panel pinned to the newest line
  useEffect(() => {
    const panel = transcriptPanelRef.current;
    if (panel) {
      panel.scrollTop = panel.scrollHeight;
    }
  }, [transcriptLines]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
      }
      if (callRef.current) {
        localHangUpRef.current = true;
        callRef.current.disconnect();
      }
      if (deviceRef.current) {
        deviceRef.current.destroy();
      }
    };
  }, []);

  const formatPhoneNumber = (phone: string): string => {
    // Format phone number for display (e.g., +1 (555) 123-4567)
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
      return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
    }
    return phone;
  };

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const startDurationTimer = () => {
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
    }
    setCallDuration(0);
    durationIntervalRef.current = setInterval(() => {
      setCallDuration((prev) => prev + 1);
    }, 1000);
  };

  const stopDurationTimer = () => {
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
      durationIntervalRef.current = null;
    }
  };

  // Takes the call id explicitly: initiateCall() creates the row and wires the
  // SDK handlers in the same closure, before setCallId() has propagated, so
  // reading the `callId` state here would post to the previous call on redial.
  const updateCallStatus = async (
    targetCallId: string,
    status: string,
    options: { twilioCallSid?: string; endedBy?: CallEndedBy } = {},
  ) => {
    if (!targetCallId) return;

    try {
      const token = await getAccessTokenSilently();
      await api.post(
        `/calls/${targetCallId}/status`,
        buildCallStatusPayload(status, options),
        { headers: { Authorization: `Bearer ${token}` } },
      );
    } catch (error) {
      console.error('Failed to update call status:', error);
    }
  };

  const reportCallEnded = (targetCallId: string) => {
    const endedBy = resolveCallEndedBy({
      localHangUp: localHangUpRef.current,
      hadError: callErrorRef.current,
    });
    updateCallStatus(targetCallId, 'ended', { endedBy });
  };

  // The Voice SDK only reports an opaque `ConnectionError (31005) HANGUP` when
  // an outbound leg is rejected. The real reason (e.g. a geographic-permission
  // block, Twilio 13227) is written to the call row by the /twilio/status-callback
  // webhook shortly after. Poll GET /calls/:id a few times and, once a reason is
  // available, replace the opaque error text with operator-actionable guidance.
  const refineFailureReason = async (callIdArg: string) => {
    if (!callIdArg) return;
    for (let attempt = 0; attempt < 4; attempt++) {
      try {
        const token = await getAccessTokenSilently();
        const { data } = await api.get(`/calls/${callIdArg}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const call = data?.call;
        if (call) {
          const info = describeTwilioDialError(
            call.twilioErrorCode,
            call.twilioErrorMessage,
            call.twilioStatus,
          );
          if (info) {
            setErrorMessage(info.reason);
            return;
          }
        }
      } catch (error) {
        console.error('Failed to fetch call failure reason:', error);
      }
      await new Promise((resolve) => setTimeout(resolve, 1500));
    }
  };

  const handleEditPhone = () => {
    setEditedPhoneNumber(phoneNumber);
    setIsEditingPhone(true);
  };

  const handleSavePhone = () => {
    // Normalize to E.164 format
    let normalized = editedPhoneNumber.trim();

    // Convert UK domestic (0131...) to international (+44131...)
    if (normalized.startsWith('0')) {
      normalized = `+44${normalized.substring(1)}`;
    }
    // Add + if missing
    else if (!normalized.startsWith('+')) {
      normalized = `+${normalized}`;
    }

    setPhoneNumber(normalized);
    setIsEditingPhone(false);
  };

  const handleCancelEditPhone = () => {
    setEditedPhoneNumber(phoneNumber);
    setIsEditingPhone(false);
  };

  const initiateCall = async () => {
    if (translationEnabled && !targetLanguage.trim()) {
      setErrorMessage(
        'Enter the language they speak before dialling, or untick Live translation.',
      );
      return;
    }
    try {
      setCallStatus('initializing');
      setErrorMessage('');

      const token = await getAccessTokenSilently();

      // Step 1: Send system message to agent (only when guidance is enabled)
      if (guidanceEnabled && threadId && agentId) {
        try {
          await api.post(
            '/agents',
            {
              agent_id: agentId,
              thread_id: threadId,
              message: `SYSTEM_MESSAGE The ops team member is now calling ${phoneNumber}. 

🎧 LIVE PHONE COACHING MODE 🎧

You are now the VOICE of the ops team member. They will read your words EXACTLY as you write them to the person on the phone.

HOW IT WORKS:
- Messages prefixed with "📞 [Them]:" = what the person at ${phoneNumber} said. RESPOND to these with a SAY: line.
- Messages prefixed with "🎤 [You]:" = what our ops person has ALREADY said out loud. These are for your context only — DO NOT respond to them, just note they were said so you don't repeat yourself or contradict.
- Your response = the next line for our ops person to say (WILL BE READ VERBATIM)
- All transcripts arrive in English regardless of the language actually spoken on the call (Whisper translates on the fly). Reply in English unless the ops person explicitly switches to another language.

⚡ SPEED IS EVERYTHING ⚡

Phone conversations are FAST. The person is waiting. Every second of silence is awkward.

RULES:
1. START every response with "SAY:" - this is what they read out loud
2. ONE sentence max. Two if absolutely necessary.
3. NO thinking, NO explaining, NO context - just the line to say
4. Use contractions: "I'd like" not "I would like"
5. Short dates: "Friday 7pm" not "Friday the 15th of November at 7 o'clock"
6. No filler: Skip "um", "so", "well" unless natural

EXAMPLES OF GOOD RESPONSES:
- SAY: Hi, I'm calling to book a table for 4 on Friday at 7.
- SAY: Perfect, the name's Smith.
- SAY: Do you have anything at 8 instead?
- SAY: Great, thanks very much. Bye!

EXAMPLES OF BAD RESPONSES:
❌ "You should ask them about availability and then..." (NO - give the exact words!)
❌ "SAY: Hello there, I hope you're having a wonderful day, I was wondering if I might possibly enquire about..." (TOO LONG)
❌ "Let me think about how to respond to that..." (NO THINKING - just give the line!)

HANDLING THE CONVERSATION:
- They say something unexpected? Give the next line immediately.
- Need info? "SAY: What times do you have?"
- Confirming? "SAY: So that's Friday 7pm for 4 people under Smith, yeah?"
- Ending? "SAY: Lovely, thanks. Bye!"

The ops person is waiting. Be instant. Be brief. Just give them the words.

Reply "Ready" to confirm.`,
            },
            { headers: { Authorization: `Bearer ${token}` } },
          );
        } catch (error) {
          console.error('Failed to send system message:', error);
          // Continue anyway
        }
      }

      // Step 2: Show countdown (15 seconds) — only when guidance is enabled
      if (guidanceEnabled) {
        for (let i = 15; i >= 1; i--) {
          setCountdown(i);
          await new Promise((resolve) => setTimeout(resolve, 1000));
        }
        setCountdown(null);
      }

      // Step 3: Initiate the actual call
      const response = await api.post(
        '/calls/initiate',
        {
          taskId,
          phoneNumber,
          contactName,
          coachingEnabled: guidanceEnabled,
          translationEnabled,
          targetLanguage: translationEnabled ? targetLanguage : undefined,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (!response.data.success) {
        throw new Error(response.data.error || 'Failed to initiate call');
      }

      const { callId: newCallId, token: twilioToken } = response.data;
      setCallId(newCallId);
      localHangUpRef.current = false;
      callErrorRef.current = false;
      setTranscriptLines([]);
      setTranslatedMessage('');
      setTranslationError('');
      setSuggestedReplies([]);

      // Initialize Twilio Device
      const device = new Device(twilioToken, {
        logLevel: 1,
        codecPreferences: [Call.Codec.Opus, Call.Codec.PCMU],
      });

      deviceRef.current = device;

      // Register device event handlers
      device.on('registered', () => {
        console.log('Twilio Device registered');
      });

      device.on('error', (error: any) => {
        console.error('Twilio Device error:', error);
        setCallStatus('error');
        setErrorMessage('Device error: ' + error.message);
      });

      // Register the device
      await device.register();

      // Make the call
      setCallStatus('connecting');
      await updateCallStatus(newCallId, 'connecting');

      // Pass `humanTaskCallId` through Twilio so the backend's /call-connect
      // webhook can pin the CallSid to the EXACT humanTaskCalls row without
      // having to guess by phone number. This makes the backend webhook a
      // reliable second-chance writer of twilioCallSid alongside the
      // updateCallStatus() POST below — whichever one wins the race wins
      // (the backend race-guards on twilioCallSid IS NULL). Without this,
      // if the frontend POST fails or is delayed, transcription setup in
      // routes/twilio-media-stream.ts (getCallContext) times out at 10s
      // and the live coaching flow breaks entirely.
      //
      // Use the LOCAL `newCallId`, not the React state `callId` — the
      // setCallId(newCallId) above hasn't propagated yet, so reading the
      // state here would send an empty string and the backend would refuse
      // to pin.
      const call = await device.connect({
        params: {
          To: phoneNumber,
          humanTaskCallId: newCallId,
        },
      });

      callRef.current = call;

      // Register call event handlers
      call.on('accept', () => {
        console.log('Call accepted');
        setCallStatus('connected');
        startDurationTimer();
        updateCallStatus(newCallId, 'connected', {
          twilioCallSid: call.parameters.CallSid,
        });
      });

      call.on('disconnect', () => {
        console.log('Call disconnected');
        setCallStatus('ended');
        stopDurationTimer();
        reportCallEnded(newCallId);
      });

      call.on('cancel', () => {
        console.log('Call cancelled');
        callErrorRef.current = true;
        setCallStatus('ended');
        stopDurationTimer();
        reportCallEnded(newCallId);
      });

      call.on('reject', () => {
        console.log('Call rejected');
        callErrorRef.current = true;
        setCallStatus('error');
        setErrorMessage('Call was rejected');
        reportCallEnded(newCallId);
        refineFailureReason(newCallId);
      });

      call.on('error', (error: any) => {
        console.error('Call error:', error);
        callErrorRef.current = true;
        setCallStatus('error');
        setErrorMessage('Call error: ' + error.message);
        stopDurationTimer();
        reportCallEnded(newCallId);
        refineFailureReason(newCallId);
      });
    } catch (error: any) {
      console.error('Failed to initiate call:', error);
      setCallStatus('error');

      // Handle different error types
      let message = 'Failed to initiate call';
      if (error?.response?.data?.error) {
        message = error.response.data.error;
      } else if (error?.message) {
        message = error.message;
      } else if (typeof error === 'string') {
        message = error;
      }

      setErrorMessage(message);
    }
  };

  const hangUpCall = () => {
    localHangUpRef.current = true;
    if (callRef.current) {
      callRef.current.disconnect();
    }
    stopDurationTimer();
  };

  const sendDigit = (digit: string) => {
    if (callRef.current && callStatus === 'connected') {
      try {
        callRef.current.sendDigits(digit);
        console.log('Sent DTMF digit:', digit);
      } catch (error) {
        console.error('Failed to send digit:', error);
      }
    }
  };

  // "First thing to say" openers are generated as soon as the operator has
  // confirmed the language (stopped typing for a moment) — before dialling —
  // so they're ready when the call is answered.
  const lastOpenersLanguageRef = useRef<string>('');
  useEffect(() => {
    const language = targetLanguage.trim();
    if (!translationEnabled || !language) return;
    if (language === lastOpenersLanguageRef.current) return;

    const timer = setTimeout(async () => {
      lastOpenersLanguageRef.current = language;
      setOpenersLoading(true);
      try {
        const token = await getAccessTokenSilently();
        const { data } = await api.post(
          `/calls/tasks/${taskId}/openers`,
          { targetLanguage: language, contactName },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (data.success && Array.isArray(data.openers)) {
          setOpeners(data.openers);
        }
      } catch (error) {
        console.error('Failed to fetch call openers:', error);
      } finally {
        setOpenersLoading(false);
      }
    }, 800);

    return () => clearTimeout(timer);
  }, [
    translationEnabled,
    targetLanguage,
    taskId,
    contactName,
    getAccessTokenSilently,
  ]);

  // Load a suggestion into the typed-translation box: the translated line is
  // ready to read aloud immediately, and the English sits in the input in
  // case the operator wants to tweak it and re-translate.
  const applySuggestion = (reply: SuggestedReply) => {
    setTypedMessage(reply.english);
    setTranslatedMessage(reply.translated);
    setTranslationError('');
  };

  const translateTypedMessage = async () => {
    const text = typedMessage.trim();
    if (!text || !callId || isTranslating) return;

    setIsTranslating(true);
    setTranslationError('');

    try {
      const token = await getAccessTokenSilently();
      const { data } = await api.post(
        `/calls/${callId}/translate`,
        { text },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!data.success) {
        throw new Error(data.error || 'Translation failed');
      }
      setTranslatedMessage(data.translation);
      setTypedMessage('');
    } catch (error: any) {
      console.error('Failed to translate message:', error);
      setTranslationError(
        error?.response?.data?.error || error?.message || 'Translation failed',
      );
    } finally {
      setIsTranslating(false);
    }
  };

  const requestAiGuidance = async () => {
    if (!transcriptionInput.trim() || !callId) return;

    setIsRequestingGuidance(true);

    try {
      const token = await getAccessTokenSilently();
      await api.post(
        `/calls/${callId}/request-guidance`,
        { transcription: transcriptionInput },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      // Clear input after sending
      setTranscriptionInput('');
      console.log('AI guidance requested - check the chat for response');
    } catch (error) {
      console.error('Failed to request guidance:', error);
      setErrorMessage('Failed to request AI guidance');
    } finally {
      setIsRequestingGuidance(false);
    }
  };

  const getStatusDisplay = (): { text: string; color: string } => {
    switch (callStatus) {
      case 'loading':
        return { text: 'Loading...', color: '#6c757d' };
      case 'idle':
        return { text: 'Ready to call', color: '#6c757d' };
      case 'initializing':
        return { text: 'Initializing...', color: '#0066cc' };
      case 'connecting':
        return { text: 'Connecting...', color: '#0066cc' };
      case 'connected':
        return { text: 'Connected', color: '#28a745' };
      case 'ended':
        return { text: 'Call ended', color: '#6c757d' };
      case 'error':
        return { text: 'Error', color: '#dc3545' };
      default:
        return { text: 'Unknown', color: '#6c757d' };
    }
  };

  const status = getStatusDisplay();
  const isCallActive =
    callStatus === 'connecting' || callStatus === 'connected';

  useEffect(() => {
    onCallActiveChange?.(isCallActive);
  }, [isCallActive, onCallActiveChange]);
  const isInitializing = callStatus === 'initializing';
  const isLoading = callStatus === 'loading';

  // The side panel appears as soon as openers are being prepared (language
  // confirmed, pre-dial) and stays for the duration of the call.
  const showSuggestionsPanel =
    translationEnabled &&
    (isCallActive || openersLoading || openers.length > 0);

  const renderSuggestionList = (replies: SuggestedReply[]) => (
    <div className='suggestion-list'>
      {replies.map((reply, index) => (
        <button
          key={`${reply.english}-${index}`}
          className='suggestion-item'
          onClick={() => applySuggestion(reply)}
          title='Click to load into the translate box'
        >
          <span className='suggestion-translated'>{reply.translated}</span>
          <span className='suggestion-english'>{reply.english}</span>
        </button>
      ))}
    </div>
  );

  return (
    <div
      className={`human-task-phone-dialer${showSuggestionsPanel ? ' with-suggestions' : ''}`}
    >
      <div className='dialer-main'>
        {cancelled && (
          <div className='dialer-cancelled-banner'>
            <div className='dialer-cancelled-banner-title'>
              🚫 TASK CANCELLED by the requester
            </div>
            <div className='dialer-cancelled-banner-detail'>
              {isCallActive
                ? 'Wrap up the call quickly but politely — make NO further commitments, and do NOT just hang up.'
                : 'Do not place any new calls for this task.'}
            </div>
          </div>
        )}
        {isOpsAdmin && (
          <label className='guidance-toggle'>
            <input
              type='checkbox'
              checked={guidanceEnabled}
              onChange={(e) => setGuidanceEnabled(e.target.checked)}
              disabled={isCallActive || isInitializing}
            />
            <span>Enable AI guidance</span>
          </label>
        )}
        <label className='guidance-toggle translation-toggle'>
          <input
            type='checkbox'
            checked={translationEnabled}
            onChange={(e) => setTranslationEnabled(e.target.checked)}
            disabled={isCallActive || isInitializing}
          />
          <span>Live translation</span>
        </label>
        {translationEnabled && (
          <div className='translation-language'>
            <label htmlFor='dialer-target-language'>They speak</label>
            <input
              id='dialer-target-language'
              type='text'
              value={targetLanguage}
              onChange={(e) => setTargetLanguage(e.target.value)}
              placeholder='e.g. Spanish, Japanese, French…'
              disabled={isCallActive || isInitializing}
            />
          </div>
        )}
        <div className='dialer-header'>
          <div className='contact-info'>
            <div className='phone-icon'>
              <FontAwesomeIcon icon={faPhone} />
            </div>
            <div className='contact-details'>
              {contactName && <div className='contact-name'>{contactName}</div>}
              {isEditingPhone ? (
                <div className='phone-number-edit'>
                  <input
                    type='tel'
                    value={editedPhoneNumber}
                    onChange={(e) => setEditedPhoneNumber(e.target.value)}
                    className='phone-input'
                    placeholder='+44...'
                    autoFocus
                  />
                  <button
                    onClick={handleSavePhone}
                    className='btn-save-phone'
                    title='Save'
                  >
                    ✓
                  </button>
                  <button
                    onClick={handleCancelEditPhone}
                    className='btn-cancel-phone'
                    title='Cancel'
                  >
                    ✕
                  </button>
                </div>
              ) : (
                <div className='phone-number'>
                  {formatPhoneNumber(phoneNumber)}
                  {!isCallActive && !isInitializing && (
                    <button
                      onClick={handleEditPhone}
                      className='btn-edit-phone'
                      title='Edit phone number'
                    >
                      ✏️
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className='dialer-status'>
          <div
            className='status-indicator'
            style={{ backgroundColor: status.color }}
          >
            {isInitializing && (
              <FontAwesomeIcon icon={faSpinner} spin className='status-icon' />
            )}
          </div>
          <div className='status-text' style={{ color: status.color }}>
            {status.text}
          </div>
        </div>

        {callStatus === 'connected' && (
          <div className='call-duration'>
            <div className='duration-label'>Duration</div>
            <div className='duration-time'>{formatDuration(callDuration)}</div>
          </div>
        )}

        {errorMessage && (
          <div className='error-message'>
            <strong>Error:</strong> {errorMessage}
          </div>
        )}

        <div className='dialer-controls'>
          {!isCallActive && !isInitializing && !isLoading && (
            <button
              className='btn-call'
              onClick={initiateCall}
              disabled={countdown !== null || cancelled}
              title={
                cancelled
                  ? 'This task was cancelled by the requester — no new calls.'
                  : undefined
              }
            >
              <FontAwesomeIcon icon={faPhone} />
              <span>{countdown !== null ? countdown : 'Call Now'}</span>
            </button>
          )}

          {isCallActive && (
            <button className='btn-hang-up' onClick={hangUpCall}>
              <FontAwesomeIcon icon={faPhoneSlash} />
              <span>Hang Up</span>
            </button>
          )}
        </div>

        {translationEnabled && (isCallActive || callStatus === 'ended') && (
          <div className='live-transcript'>
            <div className='live-transcript-header'>
              Live transcript{' '}
              <span className='transcript-note'>(translated to English)</span>
            </div>
            <div className='live-transcript-lines' ref={transcriptPanelRef}>
              {transcriptLines.length === 0 ? (
                <div className='transcript-empty'>
                  Waiting for the other side to speak…
                </div>
              ) : (
                transcriptLines.map((line) => (
                  <div
                    key={line.utteranceId}
                    className={`transcript-line ${line.side} ${
                      line.isFinal ? 'final' : 'partial'
                    }`}
                  >
                    <span className='transcript-speaker'>Them</span>
                    <span className='transcript-text'>{line.text}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {callStatus === 'connected' && (
          <div className='dial-pad'>
            <div className='dial-pad-header'>
              Press keys to navigate phone menu:
            </div>
            <div className='dial-pad-grid'>
              {['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'].map(
                (digit) => (
                  <button
                    key={digit}
                    className='dial-pad-button'
                    onClick={() => sendDigit(digit)}
                  >
                    {digit}
                  </button>
                ),
              )}
            </div>
          </div>
        )}

        <div className='dialer-instructions'>
          {callStatus === 'idle' && (
            <p>Click "Call Now" to initiate the call.</p>
          )}
          {callStatus === 'connected' && (
            <p>
              You are now connected. Use the dial pad above to navigate phone
              menus. Speak clearly and follow the task instructions.
            </p>
          )}
          {callStatus === 'ended' && (
            <p>
              The call has ended. Use the task completion button when you're
              ready to mark this task as complete.
            </p>
          )}
        </div>
      </div>

      {showSuggestionsPanel && (
        <div className='dialer-suggestions'>
          {isCallActive && (
            <div className='typed-translation'>
              <div className='typed-translation-header'>
                Type in English — read the translation aloud
              </div>
              <div className='typed-translation-input-row'>
                <input
                  type='text'
                  value={typedMessage}
                  onChange={(e) => setTypedMessage(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      translateTypedMessage();
                    }
                  }}
                  placeholder={`What do you want to say${targetLanguage ? ` in ${targetLanguage}` : ''}?`}
                  disabled={isTranslating}
                />
                <button
                  onClick={translateTypedMessage}
                  disabled={isTranslating || !typedMessage.trim()}
                >
                  {isTranslating ? (
                    <FontAwesomeIcon icon={faSpinner} spin />
                  ) : (
                    'Translate'
                  )}
                </button>
              </div>
              {translationError && (
                <div className='typed-translation-error'>
                  {translationError}
                </div>
              )}
              {translatedMessage && (
                <div className='typed-translation-result'>
                  {translatedMessage}
                </div>
              )}
            </div>
          )}
          <div className='suggestions-section'>
            <div className='suggestions-header'>First thing to say</div>
            {openersLoading ? (
              <div className='suggestions-empty'>
                <FontAwesomeIcon icon={faSpinner} spin /> Preparing openers…
              </div>
            ) : openers.length > 0 ? (
              renderSuggestionList(openers)
            ) : (
              <div className='suggestions-empty'>No openers available.</div>
            )}
          </div>
          {isCallActive && (
            <div className='suggestions-section'>
              <div className='suggestions-header'>Suggested replies</div>
              {suggestedReplies.length > 0 ? (
                renderSuggestionList(suggestedReplies)
              ) : (
                <div className='suggestions-empty'>
                  Suggestions will appear here when the other side says
                  something with an obvious response.
                </div>
              )}
            </div>
          )}
          <div className='suggestions-hint'>
            Click a suggestion to load it into the translate box.
          </div>
        </div>
      )}
    </div>
  );
};

export default HumanTaskPhoneDialer;
